import React, { useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { apiUrl } from '../config/api';
import { AuthContext } from '../context/AuthContext';
import { formatDateDMY, formatDateTimeDMY } from '../utils/dateUtils';
import { useSortableTable, TABLE_HEAD_ROW, TABLE_HEAD_TEXT, TABLE_ROW_LINE } from './UI/tableSort';
import { toCurrency } from '../utils/adminMetrics';
import DisplayField from './UI/DisplayField';
import CollapsibleRow from './UI/CollapsibleRow';
import ConfirmDialog from './UI/ConfirmDialog';
import AffordabilityCheck, { useLoanAffordability } from './affordability/AffordabilityCheck';
import { OUTCOME_STYLES, recordView as viewOfRecord } from './affordability/affordabilityView';
import { monthLabel } from './rejectionMessage';

// The verification calculator's verdict on a loan, as the API sends it on
// staff loan responses (loan.calculator). Staff only: a loan passed in
// without it is fetched again from GET /api/loans/:id, which has it, and so
// is every loan once reloadKey changes (after the calculation is run again).
export const useLoanCalculator = (loan, reloadKey = 0) => {
  const { user } = useContext(AuthContext) || {};
  const perms = user?.permissions || [];
  const isStaff = perms.includes('*') || perms.includes('ViewLoans') || perms.includes('ManageLoans');
  const given = loan?.calculator;
  const [fetched, setFetched] = useState(null);

  useEffect(() => {
    setFetched(null);
    if (!isStaff || !loan?.id || (given !== undefined && !reloadKey)) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(apiUrl(`/api/loans/${loan.id}`), { headers: { Authorization: `Bearer ${token}` } });
        if (!cancelled) setFetched(res.data?.calculator || null);
      } catch {
        // Not available — no banner.
      }
    })();
    return () => { cancelled = true; };
  }, [isStaff, loan?.id, given, reloadKey]);

  if (!isStaff) return null;
  return given !== undefined && !reloadKey ? given : fetched;
};

// In-page confirmation (UI/ConfirmDialog), in the style of the app's other
// confirm dialogs.
const ConfirmRerun = ({ onCancel, onConfirm }) => (
  <ConfirmDialog title="Run the calculation again" confirmLabel="Yes, run it again" onCancel={onCancel} onConfirm={onConfirm}>
    <p>Redo the calculation with the payslip and bank statement as already read?</p>
  </ConfirmDialog>
);

// Until the loan is approved; the loan itself (amount, term, references,
// signatures, documents) is never changed, and a rerun that fails keeps the
// previous result (the API notes it on the loan).
const RECALCULABLE_STATUSES = ['Pending', 'Awaiting Staff', 'Awaiting Approval', 'Awaiting Client'];

// Runs the verification calculator again on the loan's payslip and bank
// statement (POST /api/loans/:id/recalculate), follows its progress, and
// calls onDone when it has finished. Staff who may edit loans, while the loan
// is with staff or the client.
// compact: just the button and a one-line status, for a page header.
// calculator: the loan's verdict, when the loan object at hand does not carry
// credit_assessment_id (some screens open a loan from a list without it).
export const RecalculateCalculation = ({ loan, onDone, className = '', compact = false, calculator = null }) => {
  const { user } = useContext(AuthContext) || {};
  const perms = user?.permissions || [];
  const allowed = perms.includes('*') || perms.includes('EditLoan') || perms.includes('ManageLoans');
  const [stage, setStage] = useState(null);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(null);
  const [asking, setAsking] = useState(false);

  const hasCalculation = Boolean(loan?.credit_assessment_id || calculator);
  if (!allowed || !loan?.id || !hasCalculation) return null;
  // In the header the button stays, greyed out with the reason, once the
  // loan is past the stage where the calculation can be run again.
  const blockedWhy = loan?.status && !RECALCULABLE_STATUSES.includes(loan.status)
    ? `The calculation can be run again only while a loan is ${RECALCULABLE_STATUSES.join(', ')}. This loan is ${loan.status}.`
    : null;
  if (blockedWhy && !compact) return null;

  const run = async () => {
    setAsking(false);
    setError(null);
    setDone(null);
    setStage('Starting');
    const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
    let started = false;
    try {
      const start = await axios.post(apiUrl(`/api/loans/${loan.id}/recalculate`), {}, { headers });
      started = true;
      const id = start.data?.assessment_id;
      for (;;) {
        await new Promise((r) => setTimeout(r, 3000));
        let res;
        try {
          res = await axios.get(apiUrl(`/api/prequalify/${id}`), { headers });
        } catch (pollErr) {
          // A dropped connection while it runs: keep following it.
          if (!pollErr?.response) continue;
          throw pollErr;
        }
        const a = res.data?.assessment || res.data || {};
        if (a.status === 'Processing') { setStage(a.progress_stage || 'Working'); continue; }
        setStage(null);
        if (a.status === 'Completed') setDone('The calculation has been run again.');
        else setError(a.error_message || (a.status === 'Blocked' ? 'The documents did not pass the checks this time.' : 'The calculation could not be finished.'));
        onDone?.();
        return;
      }
    } catch (e) {
      setStage(null);
      const status = e?.response?.status;
      const said = typeof e?.response?.data === 'object' ? e.response.data?.error : null;
      if (said) setError(said);
      else if (!e?.response) setError('Could not reach the server. Check the connection and try again.');
      else if (status === 404 && !started) setError('The server does not have this yet: it may still be updating. Try again in a few minutes.');
      else if (status === 403) setError('You do not have permission to run the calculation again.');
      else setError(`${started ? 'The calculation started, but its progress could not be read' : 'Could not run the calculation again'} (error ${status}).`);
    }
  };

  if (compact) {
    const line = stage || done || error;
    return (
      <div className={`flex flex-col items-start flex-shrink-0 ${className}`}>
        <button
          type="button"
          onClick={() => setAsking(true)}
          disabled={!!stage || !!blockedWhy}
          title={blockedWhy || 'Read the payslip and bank statement again and redo every check. The loan itself is not changed.'}
          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-400 disabled:opacity-60 disabled:cursor-wait whitespace-nowrap"
        >
          <svg className={`h-4 w-4 ${stage ? 'animate-spin' : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h5M20 20v-5h-5M5.1 15a7 7 0 0011.9 2.9L20 15M4 9l3-2.9A7 7 0 0118.9 9" />
          </svg>
          {stage ? 'Running…' : 'Run calculation again'}
        </button>
        {line && (
          <span className={`mt-0.5 max-w-[16rem] truncate text-[11px] ${error ? 'text-rose-600 dark:text-rose-400' : done ? 'text-emerald-600 dark:text-emerald-400' : 'text-indigo-700 dark:text-indigo-300'}`} title={line}>
            {line}
          </span>
        )}
        {asking && <ConfirmRerun onCancel={() => setAsking(false)} onConfirm={run} />}
      </div>
    );
  }

  return (
    <div className={`rounded-lg border-2 border-indigo-300 dark:border-indigo-700/60 bg-indigo-50 dark:bg-indigo-900/20 p-3 ${className}`}>
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex-1 min-w-[12rem]">
          <p className="text-sm font-semibold text-indigo-900 dark:text-indigo-100">Verification calculator</p>
          <p className="text-xs text-indigo-800 dark:text-indigo-200">
            Read the payslip and bank statement again and redo every check, for example after a misread.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setAsking(true)}
          disabled={!!stage}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-400 disabled:opacity-60 disabled:cursor-wait"
        >
          <svg className={`h-4 w-4 ${stage ? 'animate-spin' : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h5M20 20v-5h-5M5.1 15a7 7 0 0011.9 2.9L20 15M4 9l3-2.9A7 7 0 0118.9 9" />
          </svg>
          {stage ? 'Running the calculation…' : 'Run the calculation again'}
        </button>
      </div>
      {asking && <ConfirmRerun onCancel={() => setAsking(false)} onConfirm={run} />}
      {stage && <p className="mt-2 text-xs text-indigo-800 dark:text-indigo-200">{stage}</p>}
      {done && <p className="mt-2 text-xs font-medium text-emerald-700 dark:text-emerald-400">{done}</p>}
      {error && <p className="mt-2 text-xs font-medium text-rose-700 dark:text-rose-400">{error}</p>}
    </div>
  );
};

const formatPoints = (n) => (Number.isInteger(n) ? String(n) : Number(n).toFixed(1));

// Green for what counts in the client's favour, a soft red for what counts
// against: quiet colours, so the panel informs rather than alarms.
const GOOD = 'text-emerald-700 dark:text-emerald-400';
const BAD = 'text-rose-600 dark:text-rose-300';
const GOOD_ROW = 'bg-emerald-50/70 dark:bg-emerald-900/15';
const BAD_ROW = 'bg-rose-50/70 dark:bg-rose-900/15';
// A plain figure: neither for nor against (on a decline, what is left over
// is a fact, not a go-ahead).
const NEUTRAL = 'text-gray-600 dark:text-slate-300';
const TH = { className: `px-2 py-1.5 ${TABLE_HEAD_TEXT}` };
const TH_RIGHT = { ...TH, align: 'right' };

// A section as one row (UI/CollapsibleRow): its heading and a one-line
// summary; a click opens it to show the details.
export const Section = CollapsibleRow;

// A small table; a click on a column header sorts it. columns: { label, key,
// value(row) for sorting, render(row), align, className }.
const MiniTable = ({ columns, rows, rowClass = null }) => {
  const table = useSortableTable(rows);
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs border-collapse">
        <thead>
          <tr className={TABLE_HEAD_ROW}>
            {columns.map((c) => table.th(c.label, c.key, c.value || ((r) => r[c.key]), c.align === 'right' ? TH_RIGHT : TH))}
          </tr>
        </thead>
        <tbody>
          {table.rows.map((r, i) => (
            <tr key={i} className={`${TABLE_ROW_LINE} ${rowClass ? rowClass(r) : ''}`}>
              {columns.map((c) => (
                <td key={c.key} className={`px-2 py-1.5 ${c.align === 'right' ? 'text-right tabular-nums whitespace-nowrap' : ''} ${c.className || ''}`}>
                  {c.render ? c.render(r) : r[c.key]}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// The payslip's net pay, what was asked, the income used, and the salary the
// bank statement shows each month.
const IncomeSection = ({ details }) => {
  const { income, salaries } = details;
  const found = salaries.filter((s) => s.amount !== null).length;
  // Matching the payslip (the affordability check), when it said; else found.
  const checked = salaries.some((s) => s.matches === true || s.matches === false);
  const matched = salaries.filter((s) => s.matches === true).length;
  const due = salaries.filter((s) => s.matches !== null && s.matches !== undefined).length;
  return (
    <Section
      title="Income"
      summary={checked
        ? `Net pay ${toCurrency(income.net_pay)} · salary matches the payslip in ${matched} of ${due} months`
        : `Net pay ${toCurrency(income.net_pay)} · salary in ${found} of ${salaries.length} months`}
      tone={(checked ? matched === due : found === salaries.length) ? GOOD : BAD}
    >
      <div className="grid grid-cols-3 gap-3 mb-2">
        {[['Net pay (payslip)', income.net_pay], ['Amount asked', income.asked], ['Income used', income.used]].map(([label, value]) => (
          <DisplayField key={label} label={label}>{value ? toCurrency(value) : null}</DisplayField>
        ))}
      </div>
      {salaries.length > 0 && (
        <MiniTable
          rows={salaries}
          rowClass={(r) => (r.amount === null || r.matches === false ? BAD_ROW : '')}
          columns={[
            { label: 'Month', key: 'month', render: (r) => monthLabel(r.month) },
            { label: 'Paid in on', key: 'date', render: (r) => (r.date ? formatDateDMY(r.date) : '—'), className: 'tabular-nums' },
            {
              label: 'Salary',
              key: 'amount',
              align: 'right',
              render: (r) => (r.amount === null
                ? <span className={BAD}>None found</span>
                : r.matches === false
                  ? <span className={BAD} title={r.note || undefined}>✗ {toCurrency(r.amount)} (does not match the payslip)</span>
                  : <span className={GOOD}>✓ {toCurrency(r.amount)}</span>),
            },
          ]}
        />
      )}
    </Section>
  );
};

// Net pay, less each debt and living cost (with the statement transactions
// it came from), and what is left over: in the order it was worked out.
const AffordabilitySection = ({ a, declined = false }) => {
  const ok = a.left_over > 0;
  const cell = 'px-2 py-1.5';
  return (
    <Section
      title="What is left each month"
      summary={ok ? `Left over ${toCurrency(a.left_over)}` : `Short ${toCurrency(Math.abs(a.left_over))}`}
      tone={ok ? (declined ? NEUTRAL : GOOD) : BAD}
    >
      <div className="overflow-x-auto">
        <table className="w-full text-xs border-collapse">
          <thead>
            <tr className={TABLE_HEAD_ROW}>
              <th scope="col" className={`${cell} text-left ${TABLE_HEAD_TEXT}`}>Item</th>
              <th scope="col" className={`${cell} text-left ${TABLE_HEAD_TEXT}`}>Type</th>
              <th scope="col" className={`${cell} text-right ${TABLE_HEAD_TEXT}`}>A month</th>
            </tr>
          </thead>
          <tbody>
            <tr className={TABLE_ROW_LINE}>
              <td className={`${cell} font-medium`}>Net pay</td>
              <td className={`${cell} text-gray-500 dark:text-slate-400`}>Income</td>
              <td className={`${cell} text-right tabular-nums ${GOOD}`}>{toCurrency(a.start)}</td>
            </tr>
            {a.rows.map((r, i) => (
              <React.Fragment key={`${r.label}-${i}`}>
                <tr className={TABLE_ROW_LINE}>
                  <td className={`${cell} font-medium break-all`}>
                    {r.label}
                    {r.basis && <span className="block font-normal text-[11px] text-gray-500 dark:text-slate-400">{r.basis}</span>}
                  </td>
                  <td className={`${cell} text-gray-500 dark:text-slate-400`}>{r.kind}</td>
                  <td className={`${cell} text-right tabular-nums whitespace-nowrap ${r.added ? GOOD : BAD}`}>{r.added ? '+' : '−'} {toCurrency(r.monthly)}</td>
                </tr>
                {r.transactions.map((t, j) => (
                  <tr key={j} className={`${TABLE_ROW_LINE} text-[11px] text-gray-500 dark:text-slate-400`}>
                    <td className="pl-6 pr-2 py-1 break-all"><span className="tabular-nums mr-2">{formatDateDMY(t.date)}</span>{t.description}</td>
                    <td className="px-2 py-1" />
                    <td className="px-2 py-1 text-right tabular-nums whitespace-nowrap">{toCurrency(t.amount)}</td>
                  </tr>
                ))}
              </React.Fragment>
            ))}
            {!a.rows.length && (
              <tr className={TABLE_ROW_LINE}>
                <td colSpan={3} className={`${cell} text-gray-500 dark:text-slate-400`}>No debts or living costs were found on the bank statement.</td>
              </tr>
            )}
            <tr className={`${TABLE_ROW_LINE} ${ok ? GOOD_ROW : BAD_ROW} font-semibold`}>
              <td className={cell} colSpan={2}>{ok ? 'Left over' : 'Short'}</td>
              <td className={`${cell} text-right tabular-nums ${ok ? GOOD : BAD}`}>{ok ? toCurrency(a.left_over) : `− ${toCurrency(Math.abs(a.left_over))}`}</td>
            </tr>
            {ok && (
              <tr className={`${TABLE_ROW_LINE} ${GOOD_ROW}`}>
                <td className={cell} colSpan={2}>Most the client can repay</td>
                <td className={`${cell} text-right tabular-nums font-semibold ${GOOD}`}>{toCurrency(a.max_repayment)}</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <p className="mt-1 text-[11px] text-gray-500 dark:text-slate-400">{a.law}</p>
    </Section>
  );
};

// The scored checks, with the points each earned. With the full verdict the
// legal test is shown in the table above instead, so it is not repeated here.
const CheckList = ({ summary, skipLegal = false }) => {
  const checks = skipLegal ? summary.checks.filter((c) => c.kind !== 'legal') : summary.checks;
  if (!checks.length) return null;
  const passed = checks.filter((c) => c.passed).length;
  return (
    <Section
      title="Credit checks"
      summary={`${passed} of ${checks.length} passed${summary.score !== null ? ` · Score ${summary.score}/${summary.score_out_of}` : ''}${summary.band ? ` · band ${summary.band}${summary.band_meaning ? `: ${summary.band_meaning}` : ''}` : ''}`}
      tone={passed === checks.length ? GOOD : BAD}
    >
      <MiniTable
        rows={checks}
        columns={[
          {
            label: 'Result',
            key: 'passed',
            render: (c) => <span className={`font-semibold whitespace-nowrap ${c.passed ? GOOD : BAD}`}>{c.passed ? '✓ Passed' : '✗ Failed'}</span>,
          },
          {
            label: 'Check',
            key: 'label',
            render: (c) => (
              <span className="font-medium text-gray-800 dark:text-slate-100">
                {c.label}{c.kind === 'legal' && <span className="font-normal text-gray-500 dark:text-slate-400"> (must pass)</span>}
              </span>
            ),
          },
          { label: 'What we found', key: 'detail', className: 'text-gray-600 dark:text-slate-300' },
          {
            label: 'Points',
            key: 'points',
            align: 'right',
            value: (c) => (c.kind === 'legal' ? null : Number(c.points) / (Number(c.out_of) || 1)),
            render: (c) => (
              <span className={c.passed ? GOOD : BAD}>
                {c.kind === 'legal' ? (c.passed ? 'Pass' : 'Fail') : `${formatPoints(c.points)}/${c.out_of}`}
              </span>
            ),
          },
        ]}
      />
    </Section>
  );
};

// The legal test's result at a glance: the decision, what the client asked
// for ("How much do you need?"), what they can repay a month and what they
// qualify for over the loan's term. Only the legal test is used
// (Microlending Act / ML.S.4): no credit score or band.
const ScoreStrip = ({ calculator, months }) => {
  if (!calculator.decision && (calculator.max_repayment === null || calculator.max_repayment === undefined)) return null;
  const qualified = months && calculator.qualified_by_term ? calculator.qualified_by_term[months] : null;
  const recommended = months && calculator.recommended_by_term ? calculator.recommended_by_term[months] : null;
  const canAfford = calculator.max_repayment !== null && calculator.max_repayment !== undefined ? Number(calculator.max_repayment) : null;
  const asked = Number(calculator.requested_amount) > 0 ? Number(calculator.requested_amount) : null;
  // good / bad as the shared field's tones.
  const toneOf = (n) => (n === null || n === undefined ? null : Number(n) > 0 ? 'good' : 'alert');
  return (
    <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
      <DisplayField label="Recommendation">{calculator.decision || null}</DisplayField>
      <DisplayField label="Client asked for" tone="strong">{asked !== null ? toCurrency(asked) : null}</DisplayField>
      <DisplayField label="Statutory maximum a month" tone={toneOf(canAfford)}>{canAfford !== null ? toCurrency(canAfford) : null}</DisplayField>
      <DisplayField label={`${recommended !== null && recommended !== undefined ? 'Recommended' : 'Statutory maximum'}${months ? ` (${months} mo)` : ''}`} tone={toneOf(recommended ?? qualified)}>
        {recommended !== null && recommended !== undefined
          ? `${toCurrency(recommended)} (statutory ${toCurrency(qualified)})`
          : qualified !== null && qualified !== undefined ? toCurrency(qualified) : null}
      </DisplayField>
    </div>
  );
};

// The client's rejection message (rejectionMessage.js).
export { clientRejectionMessage } from './rejectionMessage';

// Each outcome as a small badge and a thin coloured edge on a plain panel
// (shared with the affordability check: affordability/affordabilityView.js).
const OUTCOMES = OUTCOME_STYLES;

// The client's record on earlier loans with us (the Act, s 24(4)(a)(ii):
// "history of debt repayments").
const HistorySection = ({ history }) => {
  const field = (label, value) => <DisplayField label={label}>{value}</DisplayField>;
  const has = history && history.loans_count > 0;
  const troubled = has && (history.late_instalments > 0 || history.currently_overdue_loans > 0 || history.bad_debt > 0);
  return (
    <Section
      title="Repayment history with us"
      summary={has ? `${history.loans_count} earlier loan${history.loans_count === 1 ? '' : 's'} · ${history.late_instalments || 0} late` : 'First loan with us'}
      tone={troubled ? BAD : GOOD}
    >
      {has ? (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {field('Earlier loans', `${history.loans_count} (${history.paid_off} paid off)`)}
          {field('Instalments on time', history.instalments_due > 0 ? `${history.instalments_due - history.late_instalments} of ${history.instalments_due}` : '—')}
          {field('Most days late', history.max_days_late || 0)}
          {field('Behind right now', history.currently_overdue_loans > 0 ? `Yes, ${toCurrency(history.overdue_amount)}` : 'No')}
          {field('Bad debt', history.bad_debt > 0 ? `Yes (${history.bad_debt})` : 'No')}
        </div>
      ) : (
        <p className="text-xs text-gray-500 dark:text-slate-400">
          {history ? 'First loan with us: no repayment history.' : 'Not part of this calculation (it predates the history check).'}
        </p>
      )}
    </Section>
  );
};

// The most the client may borrow on each term, the loan's own term marked.
const QualifiedSection = ({ matrix, months, declined = false }) => {
  if (!Array.isArray(matrix) || !matrix.length) return null;
  const best = Math.max(...matrix.map((r) => Number(r.max_principal) || 0));
  // The recommended amount (within the recommended instalment), when the
  // calculation gave one; the statutory maximum is the hard limit.
  const hasRecommended = matrix.some((r) => r.recommended_principal !== undefined && r.recommended_principal !== null);
  const bestRecommended = hasRecommended ? Math.max(...matrix.map((r) => Number(r.recommended_principal) || 0)) : null;
  return (
    <Section
      title="What the client qualified for"
      summary={declined
        ? `Declined · only if overridden: up to ${toCurrency(hasRecommended ? bestRecommended : best)}`
        : hasRecommended ? `Recommended up to ${toCurrency(bestRecommended)} · statutory maximum ${toCurrency(best)}` : `Up to ${toCurrency(best)}`}
      tone={declined || !(best > 0) ? BAD : GOOD}
    >
      <MiniTable
        rows={matrix}
        rowClass={(r) => (Number(r.months) === months ? 'bg-indigo-50/70 dark:bg-indigo-900/20 font-semibold' : '')}
        columns={[
          {
            label: 'Term',
            key: 'months',
            render: (r) => `${r.months} month${Number(r.months) === 1 ? '' : 's'}${Number(r.months) === months ? ' (this loan)' : ''}`,
          },
          ...(hasRecommended ? [{ label: 'Recommended', key: 'recommended_principal', align: 'right', render: (r) => toCurrency(r.recommended_principal) }] : []),
          { label: hasRecommended ? 'Statutory maximum' : 'Max amount', key: 'max_principal', align: 'right', render: (r) => toCurrency(r.max_principal) },
          { label: 'Monthly', key: 'monthly_repayment', align: 'right', render: (r) => toCurrency(r.monthly_repayment) },
          { label: 'Total', key: 'total_repayable', align: 'right', render: (r) => toCurrency(r.total_repayable) },
        ]}
      />
    </Section>
  );
};

// The calculator's figures, one row each (a click opens it): the legal ones
// only (Microlending Act / NAMFISA ML.S.4): income, what is left each month
// (with the debts and living costs behind it), the repayment history with
// us and what the client qualified for.
export const VerdictSections = ({ summary, calculator = null, months = null, declined = false, showQualified = true }) => {
  const details = summary?.details || null;
  if (!details) return null;
  return (
    <div className="mt-4 border-t border-gray-200 dark:border-slate-700">
      <IncomeSection details={details} />
      <AffordabilitySection a={details.affordability} declined={declined} />
      {calculator && <HistorySection history={calculator.loan_history} />}
      {calculator && showQualified && <QualifiedSection matrix={calculator.qualification_matrix} months={months} declined={declined} />}
    </div>
  );
};

// What an officer must check before going ahead with the calculation.
export const VerdictToCheck = ({ summary }) => {
  const toCheck = summary?.to_check || [];
  if (!toCheck.length) return null;
  return (
    <div className="mt-4 border border-amber-200 dark:border-amber-800/60 bg-amber-50/60 dark:bg-amber-900/15 px-3 py-2">
      <p className="text-xs font-semibold text-amber-900 dark:text-amber-200">Before you go ahead, check:</p>
      <ul className="mt-1 list-disc pl-5 space-y-0.5 text-xs text-amber-900/90 dark:text-amber-200/90">
        {toCheck.map((t, i) => <li key={`${t}-${i}`}>{t}</li>)}
      </ul>
    </div>
  );
};

// The loan's one calculator panel, whatever the result: the decision, the
// legal figures as rows (VerdictSections), what to check before going ahead
// and any assessor's reason. Loan Details shows it once.
// suggestionApplied: the suggested amount is already in the form (editing a
// loan), so the note says so instead of asking for it to be changed.
// The affordability check (ML.S.4), when it was saved on the loan, heads the
// panel: its decision, reasons, figures, verification and the override
// (allowOverride: false where it is only shown, as when editing a loan).
const CalculatorDeclineBanner = ({ calculator, loan = null, className = '', suggestionApplied = false, allowOverride = true }) => {
  const [affordabilityReload, setAffordabilityReload] = useState(0);
  const record = useLoanAffordability(loan?.id, `${calculator?.assessed_at || ''}|${affordabilityReload}`);
  const summary = calculator?.summary;
  if (!calculator?.declined && !summary && !record) return null;
  const calc = calculator || {};
  const reasons = Array.isArray(calc.reasons) ? calc.reasons : [];
  const risk = calc.risk_reason;
  const recordView = record ? viewOfRecord(record) : null;
  const outcome = recordView ? recordView.style : (OUTCOMES[calc.declined ? 'declined' : summary?.outcome] || OUTCOMES.approved);
  const details = summary?.details || null;
  const declinedNow = record ? record.decision === 'Declined' : Boolean(calc.declined || summary?.outcome === 'declined');
  return (
    <div className={`border border-gray-200 dark:border-slate-700 border-l-4 ${outcome.edge} bg-white dark:bg-slate-900 p-4 ${className}`} role={record?.decision === 'Declined' || calc.declined ? 'alert' : 'status'}>
      {recordView ? (
        <AffordabilityCheck
          record={record}
          loanId={loan?.id}
          loanMonths={Number(loan?.duration_months) || null}
          allowOverride={allowOverride}
          onChanged={() => setAffordabilityReload((n) => n + 1)}
        />
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-2">
            <span className={`px-2 py-0.5 border text-[11px] font-semibold ${outcome.badge}`}>{outcome.label}</span>
            <p className="text-sm font-semibold text-gray-900 dark:text-slate-100">
              {summary?.headline || calc.warning || 'Declined by the verification calculator. Approach with caution.'}
            </p>
            {calc.assessed_at && (
              <span className="ml-auto text-[11px] text-gray-500 dark:text-slate-400">Worked out {formatDateDMY(calc.assessed_at)}</span>
            )}
          </div>
          {/* Over the loan's term, or the calculator's suggested one without it. */}
          <ScoreStrip calculator={calc} months={Number(loan?.duration_months) || Number(calc.suggestion?.months) || null} />
        </>
      )}
      {/* The loan asks for more than the calculator qualifies the client for
          (after a rerun, say): the amount to offer instead. */}
      {calc.suggestion && declinedNow && (
        <div className="mt-3 border border-rose-200 dark:border-rose-800/60 bg-rose-50/50 dark:bg-rose-900/10 px-3 py-2">
          <p className="text-sm font-semibold text-rose-900 dark:text-rose-100">
            Declined: no amount can be approved until an officer overrides the decline
          </p>
          <p className="text-xs text-rose-800 dark:text-rose-200">
            If it is overridden, offer at most {toCurrency(calc.suggestion.amount)} over {calc.suggestion.months} {calc.suggestion.months === 1 ? 'month' : 'months'} (within the recommended instalment); the loan asks for {toCurrency(calc.suggestion.asked)}.
            {suggestionApplied ? ' That amount is filled in below.' : ''}
          </p>
        </div>
      )}
      {calc.suggestion && !declinedNow && (
        <div className="mt-3 border border-indigo-200 dark:border-indigo-700/60 bg-indigo-50/50 dark:bg-slate-800 px-3 py-2">
          <p className="text-sm font-semibold text-indigo-900 dark:text-indigo-100">
            Suggested amount: {toCurrency(calc.suggestion.amount)} over {calc.suggestion.months} {calc.suggestion.months === 1 ? 'month' : 'months'}
          </p>
          <p className="text-xs text-indigo-800 dark:text-indigo-200">
            The loan asks for {toCurrency(calc.suggestion.asked)}, more than the recommended amount (within the recommended instalment)
            {calc.suggestion.same_term ? ` over ${calc.suggestion.months} ${calc.suggestion.months === 1 ? 'month' : 'months'}` : ' over the loan\'s term, which gives nothing; this is the best term'}.
            {suggestionApplied
              ? ' The suggested amount is filled in below: save to keep it.'
              : ' Change the amount on the loan, or send it back to the client.'}
          </p>
        </div>
      )}
      {details ? (
        <VerdictSections
          summary={summary}
          calculator={calculator}
          months={Number(loan?.duration_months) || null}
          declined={declinedNow}
          showQualified={!recordView}
        />
      ) : summary ? (
        <>
          {/* A verdict saved before the figures came as data: its lines. */}
          {summary.why?.length > 0 && (
            <ul className="mt-3 list-disc pl-5 space-y-1 text-sm text-gray-800 dark:text-slate-200">
              {summary.why.map((w, i) => (typeof w === 'string' ? (
                <li key={`${w}-${i}`}>{w}</li>
              ) : (
                <li key={`${w.text}-${i}`}>
                  {w.text}
                  {Array.isArray(w.items) && w.items.length > 0 && (
                    <ul className="mt-1 mb-1 list-[circle] pl-5 space-y-0.5 text-xs text-gray-600 dark:text-slate-400">
                      {w.items.map((item, j) => <li key={`${item}-${j}`} className="tabular-nums">{item}</li>)}
                    </ul>
                  )}
                </li>
              )))}
            </ul>
          )}
          {summary.checks?.length > 0 && <div className="mt-4"><CheckList summary={summary} /></div>}
        </>
      ) : (
        <>
          {calc.message && <p className="mt-2 text-sm text-gray-700 dark:text-slate-300">{calc.message}</p>}
          {reasons.length > 0 && (
            <ul className="mt-2 list-disc pl-5 space-y-0.5 text-xs text-gray-600 dark:text-slate-400">
              {reasons.map((r, i) => <li key={`${r}-${i}`}>{r}</li>)}
            </ul>
          )}
        </>
      )}
      {summary && !recordView && <VerdictToCheck summary={summary} />}
      {/* With the tables, the notes only repeat them: kept for a verdict
          saved before the figures came as data. */}
      {summary && !details && reasons.length > 0 && (
        <details className="mt-3 text-xs text-gray-500 dark:text-slate-400">
          <summary className="cursor-pointer font-medium">Calculator notes (full working)</summary>
          <ul className="mt-1 list-disc pl-5 space-y-0.5">
            {reasons.map((r, i) => <li key={`${r}-${i}`}>{r}</li>)}
          </ul>
        </details>
      )}
      {risk?.text && (
        <p className="mt-3 border border-amber-200 dark:border-amber-700/60 bg-amber-50/60 dark:bg-amber-900/20 px-3 py-2 text-xs text-amber-900 dark:text-amber-200">
          <span className="font-semibold">Assessor's reason to go ahead:</span> {risk.text}
          {risk.at ? <> ({formatDateTimeDMY(risk.at)})</> : null}
        </p>
      )}
    </div>
  );
};

// The assessor's reason for going ahead with a loan the calculator declined,
// required by the API (risk_reason) when sending it back to the client.
export const RiskReasonField = ({ value, onChange }) => (
  <div className="mt-3">
    <label className="block text-sm font-medium text-rose-800 dark:text-rose-200 mb-1">
      Why are you prepared to go ahead? <span className="text-rose-600">*</span>
      <span className="block text-xs font-normal text-gray-600 dark:text-slate-400">Kept on the loan for the record — the client does not see it.</span>
    </label>
    <textarea
      className="border-rose-300 dark:border-rose-700/60 p-2 w-full h-20 resize-none focus:ring-rose-400 focus:border-transparent"
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      placeholder="Your reason for going ahead despite the calculator's decline…"
      required
    />
  </div>
);

export default CalculatorDeclineBanner;
